import { useEffect, useState } from 'react';
import type {
  AiDraftRequest,
  AiDraftResponse,
  AiOriginal,
  AiVerdict,
  ComposeState,
} from 'zitimail-shared-types';

import { api, describeError } from '../admin/api';
import { SparkIcon } from '../components/icons';

/** The message a reply answers, as the writing assistant reads it. */
export interface ReplyContext extends AiOriginal {
  /** AI Sentry's verdict on it, when it had one. */
  readonly verdict?: AiVerdict['verdict'];
}

type Tone = NonNullable<AiDraftRequest['tone']>;
type Length = NonNullable<AiDraftRequest['length']>;

/** Verdicts that refuse drafting a reply: answering at all helps the sender. */
const DANGEROUS: Record<string, string> = {
  phishing: 'phishing',
  scam: 'a scam',
  bec: 'impersonation (business email compromise)',
};

/** A sentence the user can read, from the API's lower-case refusal. */
function sentence(message: string): string {
  const text = message.trim();
  if (!text) {
    return 'Something went wrong.';
  }
  const capital = text.charAt(0).toUpperCase() + text.slice(1);
  return /[.!?]$/.test(capital) ? capital : `${capital}.`;
}

function ToneLength({
  tone,
  length,
  lengths,
  onTone,
  onLength,
  disabled,
}: {
  tone: Tone;
  length: Length;
  lengths: readonly Length[];
  onTone: (t: Tone) => void;
  onLength: (l: Length) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="ai-tools">
      <span>Tone</span>
      <select
        value={tone}
        onChange={(e) => onTone(e.target.value as Tone)}
        aria-label="Tone"
        disabled={disabled}
      >
        <option value="neutral">Neutral</option>
        <option value="warm">Warm</option>
        <option value="direct">Direct</option>
        <option value="formal">Formal</option>
      </select>
      <span>Length</span>
      <select
        value={length}
        onChange={(e) => onLength(e.target.value as Length)}
        aria-label="Length"
        disabled={disabled}
      >
        {lengths.map((l) => (
          <option key={l} value={l}>
            {l.charAt(0).toUpperCase() + l.slice(1)}
          </option>
        ))}
      </select>
    </div>
  );
}

function Prompt({
  value,
  onChange,
  onSubmit,
  placeholder,
  label,
  busy,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  placeholder: string;
  label: string;
  busy?: boolean;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="ai-prompt">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // Enter drafts; it must not submit (send) the message around it.
          if (e.key === 'Enter') {
            e.preventDefault();
            if (!disabled && !busy && value.trim()) {
              onSubmit();
            }
          }
        }}
        placeholder={placeholder}
        aria-label={label}
        disabled={disabled}
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled || busy || !value.trim()}
        aria-busy={busy}
      >
        {busy ? 'Drafting…' : 'Draft it'}
      </button>
    </div>
  );
}

/**
 * The writing assistant above the message body (mockup: `aiSection`). It only
 * ever suggests: the draft lands in the editor, and the person edits and sends
 * it themselves.
 */
export function AiStrip({
  tenant,
  senderName,
  original,
  currentDraft,
  onDraft,
}: {
  tenant: string;
  senderName?: string | undefined;
  /** Set for a reply; a new message or forward has none. */
  original?: ReplyContext | undefined;
  /** The person's own text in the editor now, so a change revises it. */
  currentDraft?: (() => string) | undefined;
  /**
   * Puts the draft in the body; false when the person declined to replace
   * their text. `revised`: it is their own text reworked, so no asking.
   */
  onDraft: (text: string, revised: boolean) => boolean;
}): JSX.Element | null {
  const base = `/tenants/${encodeURIComponent(tenant)}/ai`;
  const isReply = original !== undefined;
  const blocked = original?.verdict !== undefined && original.verdict in DANGEROUS;

  const [state, setState] = useState<ComposeState | null>(null);
  const [options, setOptions] = useState<readonly string[] | 'loading' | 'failed'>('loading');
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState<Tone>('neutral');
  // Standard for replies too: "brief" read as curt (2026-10-31).
  const [length, setLength] = useState<Length>('standard');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The length the last draft ran out of room at, when it did.
  const [cutAt, setCutAt] = useState<Length | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api<{ state: ComposeState }>(`${base}/compose`, { signal: controller.signal })
      .then((r) => setState(r.state))
      // Anything unexpected reads as "not offered": the composer works as before.
      .catch(() => {
        if (!controller.signal.aborted) {
          setState('off');
        }
      });
    return () => controller.abort();
  }, [base]);

  // Suggested replies, once the assistant is known to be on for a reply.
  useEffect(() => {
    if (state !== 'on' || !original || blocked) {
      return;
    }
    const controller = new AbortController();
    api<{ options: string[] }>(`${base}/reply-options`, {
      method: 'POST',
      body: { from: original.from, subject: original.subject, body: original.body },
      signal: controller.signal,
    })
      .then((r) => setOptions(r.options.length > 0 ? r.options.slice(0, 3) : 'failed'))
      .catch(() => {
        if (!controller.signal.aborted) {
          setOptions('failed');
        }
      });
    return () => controller.abort();
    // One fetch per compose window: the original does not change under it.
  }, [state, base, blocked]);

  if (state === null || state === 'off') {
    return null;
  }

  if (original?.verdict && blocked) {
    return (
      <div className="ai-strip blocked" role="region" aria-label="Writing assistant">
        <div className="ai-head">
          <SparkIcon />
          <span>Drafting disabled for this message</span>
          <span className="badge">Sentry</span>
        </div>
        <p className="ai-msg">
          AI Sentry classified this message as <strong>{DANGEROUS[original.verdict]}</strong>.
          Replying at all confirms your address is monitored, so ZitiMail will not help you write
          one. Report it instead.
        </p>
      </div>
    );
  }

  const title = isReply ? 'Draft a reply' : 'Write with AI';
  const lengths: readonly Length[] = isReply
    ? ['brief', 'standard', 'detailed']
    : ['standard', 'brief', 'detailed'];

  // Unavailable: visible but inert, and it says nothing about why.
  if (state === 'unavailable') {
    return (
      <div className="ai-strip off" role="region" aria-label="Writing assistant">
        <div className="ai-head">
          <SparkIcon />
          <span>{title}</span>
        </div>
        <Prompt
          value=""
          onChange={() => undefined}
          onSubmit={() => undefined}
          placeholder={isReply ? 'Describe your reply' : 'Describe what to write'}
          label={isReply ? 'What should the reply say?' : 'What should this email say?'}
          disabled
        />
        <p className="ai-msg">Currently unavailable.</p>
      </div>
    );
  }

  const draft = (instruction: string) => {
    setBusy(instruction);
    setError(null);
    setCutAt(null);
    // What is already written goes along, so "rephrase it" or "add the
    // date" reworks it instead of starting again from nothing.
    const current = currentDraft?.().trim() ?? '';
    const request: AiDraftRequest = {
      instruction,
      tone,
      length,
      ...(senderName ? { sender_name: senderName } : {}),
      ...(original
        ? { original: { from: original.from, subject: original.subject, body: original.body } }
        : {}),
      ...(current ? { current } : {}),
    };
    api<AiDraftResponse>(`${base}/draft`, { method: 'POST', body: request })
      .then((r) => {
        if (onDraft(r.text, current !== '')) {
          setPrompt('');
          if (r.cut_short) {
            setCutAt(length);
          }
        }
      })
      .catch((e: unknown) => setError(sentence(describeError(e))))
      .finally(() => setBusy(null));
  };

  const chips = isReply && Array.isArray(options) ? options : null;

  return (
    <div className="ai-strip" role="region" aria-label="Writing assistant" aria-busy={!!busy}>
      <div className="ai-head">
        <SparkIcon />
        <span>{title}</span>
        <span className="badge">On</span>
      </div>
      {isReply && options === 'loading' ? (
        <p className="ai-msg ai-quiet" aria-busy="true">
          Suggesting replies…
        </p>
      ) : (
        chips && (
          <div className="ai-chips">
            {chips.map((option) => (
              <button
                key={option}
                type="button"
                disabled={busy !== null}
                aria-busy={busy === option}
                onClick={() => draft(option)}
              >
                {option}
              </button>
            ))}
          </div>
        )
      )}
      {/* Always there, beside the suggestions: a reply of one's own, or a
          change to the draft already written ("shorter", "add the date"). */}
      <Prompt
        value={prompt}
        onChange={setPrompt}
        onSubmit={() => draft(prompt.trim())}
        placeholder={
          isReply ? 'Describe your reply, or a change to it' : 'Describe what to write, or a change'
        }
        label={isReply ? 'What should the reply say?' : 'What should this email say?'}
        busy={busy !== null}
      />
      <ToneLength
        tone={tone}
        length={length}
        lengths={lengths}
        onTone={setTone}
        onLength={setLength}
        disabled={busy !== null}
      />
      {busy !== null && (
        <p className="ai-msg ai-quiet" role="status">
          Drafting… this can take a few seconds.
        </p>
      )}
      {error && (
        <p className="ai-msg ai-error" role="alert">
          {error}
        </p>
      )}
      {cutAt && (
        <p className="ai-msg" role="status">
          {cutAt === 'detailed'
            ? 'This draft reached the longest length, so its unfinished end was left out. Ask for a shorter version, or finish it yourself.'
            : `This draft reached the ${cutAt === 'brief' ? 'Brief' : 'Standard'} length, so its unfinished end was left out. Choose Detailed and ask again for the whole of it.`}
        </p>
      )}
    </div>
  );
}
